import { AudioSettings } from "./AudioSettings.tsx";
import { HeroPortrait } from "./Portrait.tsx";
import { GameMode, HEROES, type MapId, MAPS, MODES, Phase, Team } from "@rivalwatch/shared";
import { currentGame } from "./GameView.tsx";
import { RoleIcon } from "./icons.tsx";
import { toast, ui } from "./store.ts";

/** m:ss */
const clock = (seconds: number) => {
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export function Lobby({ onPlay, onLeave }: { onPlay: () => void; onLeave: () => void }) {
  const l = ui.lobby.value;
  if (!l) return null;
  const me = l.players.find((p) => p.id === ui.you.value);
  const myTeam = me?.team ?? Team.A;
  const isHost = !!me?.host;
  const send = (m: Parameters<NonNullable<ReturnType<typeof currentGame>>["send"]>[0]) => currentGame()?.send(m);
  const inMatch = l.inMatch;
  const phase = ui.hud.value?.match?.phase;
  const invite = `${location.origin}/r/${l.room}`;
  const teams = [myTeam, myTeam === Team.A ? Team.B : Team.A];
  const s = l.settings;
  const mode = MODES[s.mode];
  const convoy = s.mode === GameMode.Convoy;

  return (
    <div class="overlay" onClick={(e) => e.target === e.currentTarget && onPlay()}>
      <section class="panel plate" aria-label="Lobby">
        <header>
          <div>
            <div class="eyebrow">
              {mode.name} · {MAPS[l.map].title} — {inMatch ? `Round ${ui.hud.value?.match?.round ?? 1} in progress${ui.hud.value?.match?.stageName ? ` at ${ui.hud.value.match.stageName}` : ""}` : "Warmup, practice while everyone joins"}
            </div>
            <h2>Lobby {l.room}</h2>
          </div>
          <div class="actions">
            <button class="btn brass" onClick={onPlay}>
              <span>{inMatch ? "Resume" : "Play"}</span>
            </button>
            <button
              class="btn ghost"
              onClick={() => {
                ui.overlay.value = "heroes";
              }}
            >
              <span>Change hero</span>
            </button>
            {isHost && !inMatch && (
              <button class="btn" onClick={() => send({ t: "start" })}>
                <span>Start match</span>
              </button>
            )}
            {isHost && inMatch && phase !== Phase.MatchEnd && (
              <button class="btn ghost" onClick={() => send({ t: "stop" })}>
                <span>End match</span>
              </button>
            )}
          </div>
        </header>

        <div class="invite">
          Invite: <span>{invite}</span>
          <button
            class="btn ghost small"
            onClick={() => {
              void navigator.clipboard?.writeText(invite);
              toast("Invite link copied");
            }}
          >
            <span>Copy link</span>
          </button>
        </div>

        <div class="teams" style={{ marginTop: "1.2rem" }}>
          {teams.map((team) => {
            const friend = team === myTeam;
            const players = l.players.filter((p) => p.team === team);
            return (
              <div class={`team ${friend ? "friend" : "foe"}`}>
                <h3>
                  {friend ? "Your team" : "Enemy team"}
                  <span style={{ display: "flex", gap: "0.4rem" }}>
                    {!friend && (
                      <button class="btn ghost small" onClick={() => send({ t: "setTeam", team })}>
                        <span>Switch</span>
                      </button>
                    )}
                    {isHost && players.length < 6 && (
                      <button class="btn ghost small" onClick={() => send({ t: "addBot", team })}>
                        <span>Add bot</span>
                      </button>
                    )}
                  </span>
                </h3>
                {players.map((p) => {
                  const def = HEROES[p.hero];
                  return (
                    <div class={`slot ${p.id === ui.you.value ? "me" : ""}`}>
                      <HeroPortrait hero={def.id} class="chip" />
                      <span class="name">
                        {p.name}
                        <small>
                          <RoleIcon role={def.role} size={13} />
                          {def.name}
                          {p.host ? " · host" : ""}
                        </small>
                      </span>
                      {!p.bot && <span class="ping">{p.ping} ms</span>}
                      {isHost && p.bot && (
                        <button class="x" aria-label={`Remove ${p.name}`} onClick={() => send({ t: "removeBot", id: p.id })}>
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
                {Array.from({ length: Math.max(0, 6 - players.length) }, () => (
                  <div class="slot" style={{ opacity: 0.35 }}>
                    <span class="chip">·</span>
                    <span class="name">Open slot</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {isHost && !inMatch && (
          <div class="settings-grid">
            <div class="field">
              <label for="map">Map</label>
              <select id="map" value={l.map} onChange={(e) => send({ t: "settings", settings: { map: e.currentTarget.value as MapId } })}>
                {[GameMode.Control, GameMode.Convoy].map((m) => (
                  <optgroup label={MODES[m].name}>
                    {Object.values(MAPS)
                      .filter((x) => x.mode === m)
                      .map((x) => (
                        <option value={x.id}>{x.title}</option>
                      ))}
                  </optgroup>
                ))}
              </select>
              <small class="hint">
                {mode.name}: {mode.blurb} {MAPS[l.map].blurb}
              </small>
            </div>
            {(MAPS[l.map].stages?.length ?? 0) > 1 && (
              <div class="field">
                <label for="firststage">First stage</label>
                <select id="firststage" value={s.firstStage} onChange={(e) => send({ t: "settings", settings: { firstStage: Number(e.currentTarget.value) } })}>
                  <option value={-1}>Random</option>
                  {MAPS[l.map].stages!.map((n, i) => (
                    <option value={i}>{n}</option>
                  ))}
                </select>
                <small class="hint">The later rounds are played on the other stages, in a random order.</small>
              </div>
            )}
            <label class="toggle">
              <input type="checkbox" checked={s.fillBots} onChange={(e) => send({ t: "settings", settings: { fillBots: e.currentTarget.checked } })} />
              Fill empty slots with bots
            </label>
            <label class="toggle">
              <input type="checkbox" checked={s.roleLock} onChange={(e) => send({ t: "settings", settings: { roleLock: e.currentTarget.checked } })} />
              Role lock (2 tank · 2 damage · 2 support)
            </label>
            <div class="field">
              <label for="herolimit">Hero limit per team</label>
              <select id="herolimit" value={s.heroLimit} onChange={(e) => send({ t: "settings", settings: { heroLimit: Number(e.currentTarget.value) } })}>
                <option value={0}>No limit</option>
                <option value={1}>1 of each</option>
                <option value={2}>2 of each</option>
              </select>
            </div>
            <div class="field">
              <label for="botskill">Bot skill</label>
              <select id="botskill" value={s.botSkill} onChange={(e) => send({ t: "settings", settings: { botSkill: Number(e.currentTarget.value) } })}>
                <option value={0.2}>Recruit</option>
                <option value={0.5}>Regular</option>
                <option value={0.8}>Veteran</option>
              </select>
            </div>
            <div class="field">
              <label for="setup">Setup time</label>
              <select
                id="setup"
                value={convoy ? s.match.convoySetupSeconds : s.match.setupSeconds}
                onChange={(e) => send({ t: "settings", settings: { match: { [convoy ? "convoySetupSeconds" : "setupSeconds"]: Number(e.currentTarget.value) } } })}
              >
                <option value={10}>10 seconds</option>
                <option value={20}>20 seconds</option>
                <option value={30}>30 seconds</option>
                <option value={40}>40 seconds</option>
              </select>
            </div>
            {convoy ? (
              <div class="field">
                <label for="pace">Attack time</label>
                <select id="pace" value={s.match.convoyTime} onChange={(e) => send({ t: "settings", settings: { match: { convoyTime: Number(e.currentTarget.value) } } })}>
                  {([["Short", 0.75], ["Standard", 1], ["Long", 1.25]] as const).map(([name, k]) => (
                    <option value={k}>
                      {name} ({clock((MAPS[l.map].attackSeconds ?? 300) * k)})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div class="field">
                <label for="pace">Round length</label>
                <select id="pace" value={s.match.secondsPerPercent} onChange={(e) => send({ t: "settings", settings: { match: { secondsPerPercent: Number(e.currentTarget.value) } } })}>
                  <option value={0.6}>Short (~60s held)</option>
                  <option value={1.2}>Standard (~120s held)</option>
                  <option value={1.8}>Long (~180s held)</option>
                </select>
              </div>
            )}
          </div>
        )}

        <AudioSettings />

        <div class="keys" aria-label="Controls">
          <span><kbd>WASD</kbd>Move</span>
          <span><kbd>Space</kbd>Jump</span>
          <span><kbd>Ctrl</kbd>Crouch</span>
          <span><kbd>LMB</kbd><kbd>RMB</kbd>Fire</span>
          <span><kbd>Shift</kbd><kbd>E</kbd>Abilities</span>
          <span><kbd>Q</kbd>Ultimate</span>
          <span><kbd>R</kbd>Reload</span>
          <span><kbd>V</kbd>Melee</span>
          <span><kbd>H</kbd>Heroes</span>
          <span><kbd>Tab</kbd>Scores</span>
          <span><kbd>Enter</kbd>Chat</span>
          <span><kbd>F3</kbd>Net graph</span>
          <span><kbd>F4</kbd>Hitboxes</span>
          <span style={{ marginLeft: "auto" }}>
            <button class="btn ghost small" onClick={onLeave}>
              <span>Leave lobby</span>
            </button>
          </span>
        </div>
      </section>
    </div>
  );
}
